// src/pages/Register.jsx
import React, { useState, useEffect } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebaseConfig";
import {
  collection,
  addDoc,
  setDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  FaBuilding, FaUserAlt, FaMapMarkedAlt, FaIdCard, FaPhone, FaEnvelope, FaCity, FaKey, FaLocationArrow, FaSearch, FaHashtag
} from "react-icons/fa";
import Campo from "../components/Campo";
import "sweetalert2/dist/sweetalert2.min.css";
import { useEmpresa } from "../context/EmpresaContext";

/** Region das callables (Gen1 default = us-central1).
 *  Se você mudar a região no deploy, ajuste aqui!
 */
const functions = getFunctions(undefined, "southamerica-east1");
const atualizarMinhasClaims = httpsCallable(functions, "atualizarMinhasClaims");

/** Backend base só é usado para a consulta de CNPJ (Focus). */
const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const apiUrl = (path) => (BACKEND_BASE ? `${BACKEND_BASE}${path}` : path);

function normalizarCidade(str) {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

async function buscarCodigoIBGE(cidade, uf) {
  if (!cidade || !uf) return "";
  try {
    const res = await fetch(`https://brasilapi.com.br/api/ibge/municipios/v1/${uf.toLowerCase()}`);
    const lista = await res.json();
    if (Array.isArray(lista)) {
      const cidadeNormalizada = normalizarCidade(cidade);
      const item = lista.find(c => normalizarCidade(c.nome) === cidadeNormalizada);
      return item ? item.codigo_ibge : "";
    }
  } catch {}
  return "";
}

function validarCNPJ(cnpj) {
  cnpj = cnpj.replace(/\D+/g, "");
  if (cnpj.length !== 14 || /^([0-9])\1+$/.test(cnpj)) return false;
  let t = cnpj.length - 2;
  let d = cnpj.substring(t);
  let d1 = parseInt(d.charAt(0)), d2 = parseInt(d.charAt(1));
  let calc = x => {
    let n = cnpj.substring(0, x);
    let s = 0, pos = x - 7;
    for (let i = x; i >= 1; i--) {
      s += parseInt(n.charAt(x - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    return (s % 11 < 2) ? 0 : 11 - (s % 11);
  };
  return calc(t) === d1 && calc(t + 1) === d2;
}

const Register = () => {
  const [empresa, setEmpresa] = useState({
    razaoSocial: "",
    nomeFantasia: "",
    regimeTributario: "1",
    cnpj: "",
    inscricaoEstadual: "ISENTO",
    inscricaoMunicipal: "",
    cnae: "",
    codigoTributarioMunicipio: "",
    itemListaServico: "",
    email: "",
    telefone: "",
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    codigoIBGE: "",
  });

  const [ieIsento, setIeIsento] = useState(true);
  const [nomeResp, setNomeResp] = useState("");
  const [emailResp, setEmailResp] = useState("");
  const [senha, setSenha] = useState("");
  const [loginPrefeitura, setLoginPrefeitura] = useState("");
  const [senhaPrefeitura, setSenhaPrefeitura] = useState("");
  const [erros, setErros] = useState({});
  const [loading, setLoading] = useState(false);
  const [buscandoCNPJ, setBuscandoCNPJ] = useState(false);
  const [sugestoesCidade, setSugestoesCidade] = useState([]);

  const navigate = useNavigate();
  const { refreshClaims } = useEmpresa(); // garante empresaId sem F5

  useEffect(() => {
    if (ieIsento) {
      setEmpresa((f) => ({ ...f, inscricaoEstadual: "ISENTO" }));
    } else if (empresa.inscricaoEstadual === "ISENTO") {
      setEmpresa((f) => ({ ...f, inscricaoEstadual: "" }));
    }
    // eslint-disable-next-line
  }, [ieIsento]);

  async function atualizarEmpresaComIBGE(propsAtualizados) {
    const atual = { ...empresa, ...propsAtualizados };
    let codigoIBGE = "";
    if (atual.cidade && atual.estado && atual.estado.length === 2) {
      codigoIBGE = await buscarCodigoIBGE(atual.cidade, atual.estado);
    }
    setEmpresa((prev) => ({ ...prev, ...propsAtualizados, codigoIBGE }));
  }

  const buscarDadosPorCNPJ = async () => {
    setBuscandoCNPJ(true);
    setErros({});
    try {
      const cnpjLimpo = (empresa.cnpj || "").replace(/\D/g, "");
      if (!validarCNPJ(empresa.cnpj)) {
        setErros({ cnpj: "CNPJ inválido" });
        setBuscandoCNPJ(false);
        return;
      }

      const url = apiUrl(`/api/focus/consultar-cnpj/${cnpjLimpo}`);
      const res = await fetch(url);
      let data;
      try {
        data = await res.json();
      } catch {
        setErros((e) => ({ ...e, cnpj: "Erro ao ler resposta da Focus." }));
        setBuscandoCNPJ(false);
        return;
      }

      if (!res.ok) {
        setErros({ cnpj: data?.mensagem || "CNPJ não encontrado ou indisponível." });
        setBuscandoCNPJ(false);
        return;
      }
      if (!data.cnpj) {
        setErros({ cnpj: "CNPJ não encontrado nos dados." });
        setBuscandoCNPJ(false);
        return;
      }

      const end = data.endereco || {};
      const newEmpresa = {
        cnpj: data.cnpj || empresa.cnpj,
        razaoSocial: data.razao_social || "",
        nomeFantasia: data.razao_social || "",
        regimeTributario: data.optante_mei ? "4" : (data.optante_simples_nacional ? "1" : "3"),
        inscricaoEstadual: ieIsento ? "ISENTO" : "",
        inscricaoMunicipal: "",
        cnae: data.cnae_principal || "",
        codigoTributarioMunicipio: "",
        itemListaServico: "",
        email: "",
        telefone: "",
        cep: end.cep ? end.cep.replace(/\D/g, "").replace(/^(\d{5})(\d{3})$/, "$1-$2") : "",
        endereco: end.logradouro || "",
        numero: end.numero || "",
        complemento: end.complemento || "",
        bairro: end.bairro || "",
        cidade: end.nome_municipio || "",
        estado: end.uf || "",
        codigoIBGE: end.codigo_ibge || "",
      };
      setEmpresa((f) => ({ ...f, ...newEmpresa }));

      if (!newEmpresa.codigoIBGE && newEmpresa.cidade && newEmpresa.estado) {
        const ibge = await buscarCodigoIBGE(newEmpresa.cidade, newEmpresa.estado);
        setEmpresa((f) => ({ ...f, codigoIBGE: ibge }));
      }
    } catch (err) {
      setErros((e) => ({ ...e, cnpj: "Erro ao buscar informações do CNPJ." }));
    }
    setBuscandoCNPJ(false);
  };

  const handleCepBlur = async () => {
    if (empresa.cep.replace(/\D/g, "").length === 8) {
      try {
        const res = await fetch(
          `https://viacep.com.br/ws/${empresa.cep.replace(/\D/g, "")}/json/`
        );
        const data = await res.json();
        if (!data.erro) {
          await atualizarEmpresaComIBGE({
            endereco: data.logradouro || "",
            bairro: data.bairro || "",
            cidade: data.localidade || "",
            estado: data.uf || "",
          });
        }
      } catch {}
    }
  };

  const handleCidadeChange = async (e) => {
    const valor = e.target.value;
    setEmpresa((f) => ({ ...f, cidade: valor, codigoIBGE: "" }));
    setSugestoesCidade([]);
    if (valor.length >= 3 && empresa.estado.length === 2) {
      try {
        const res = await fetch(
          `https://brasilapi.com.br/api/ibge/municipios/v1/${empresa.estado.toLowerCase()}`
        );
        const cidades = await res.json();
        if (Array.isArray(cidades)) {
          setSugestoesCidade(
            cidades
              .filter((c) => normalizarCidade(c.nome).startsWith(normalizarCidade(valor)))
              .map((c) => c.nome)
          );
        }
      } catch {}
    }
  };

  const handleSelecionaCidade = async (nome) => {
    await atualizarEmpresaComIBGE({ cidade: nome });
    setSugestoesCidade([]);
  };

  const handleEstadoChange = async (e) => {
    const uf = e.target.value.toUpperCase().slice(0, 2);
    await atualizarEmpresaComIBGE({ estado: uf });
  };

  useEffect(() => {
    if (empresa.cidade && empresa.estado && empresa.estado.length === 2) {
      buscarCodigoIBGE(empresa.cidade, empresa.estado).then((codigoIBGE) => {
        if (codigoIBGE && codigoIBGE !== empresa.codigoIBGE) {
          setEmpresa((e) => ({ ...e, codigoIBGE }));
        }
      });
    }
    // eslint-disable-next-line
  }, [empresa.cidade, empresa.estado]);

  const validar = () => {
    const erros = {};
    if (!empresa.razaoSocial.trim()) erros.razaoSocial = "Obrigatório";
    if (!empresa.nomeFantasia.trim()) erros.nomeFantasia = "Obrigatório";
    if (!empresa.cnpj.trim()) erros.cnpj = "Obrigatório";
    else if (!validarCNPJ(empresa.cnpj)) erros.cnpj = "CNPJ inválido";
    if (!empresa.inscricaoMunicipal.trim()) erros.inscricaoMunicipal = "Obrigatório";
    if (!empresa.cnae.trim()) erros.cnae = "Obrigatório";
    if (!empresa.codigoTributarioMunicipio.trim()) erros.codigoTributarioMunicipio = "Obrigatório";
    if (!empresa.itemListaServico.trim()) erros.itemListaServico = "Obrigatório";
    if (!empresa.email.trim()) erros.email = "Obrigatório";
    if (!empresa.telefone.trim()) erros.telefone = "Obrigatório";
    if (!empresa.cep.trim()) erros.cep = "Obrigatório";
    if (!empresa.endereco.trim()) erros.endereco = "Obrigatório";
    if (!empresa.numero.trim()) erros.numero = "Obrigatório";
    if (!empresa.bairro.trim()) erros.bairro = "Obrigatório";
    if (!empresa.cidade.trim()) erros.cidade = "Obrigatório";
    if (!empresa.estado.trim()) erros.estado = "Obrigatório";
    if (!empresa.codigoIBGE) erros.codigoIBGE = "Cidade/UF inválidos";
    if (!nomeResp.trim()) erros.nomeResp = "Obrigatório";
    if (!emailResp.trim()) erros.emailResp = "Obrigatório";
    if (!senha.trim()) erros.senha = "Obrigatório";
    if (!ieIsento && !empresa.inscricaoEstadual.trim()) erros.inscricaoEstadual = "Obrigatório";
    setErros(erros);
    return Object.keys(erros).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validar()) return;
    setLoading(true);

    try {
      // 1) cria usuário e força token fresco
      const cred = await createUserWithEmailAndPassword(auth, emailResp, senha);
      await cred.user.getIdToken(true);

      // 2) IBGE/IE consistentes
      let codigoIBGE = empresa.codigoIBGE;
      if (!codigoIBGE && empresa.cidade && empresa.estado) {
        codigoIBGE = await buscarCodigoIBGE(empresa.cidade, empresa.estado);
      }

      const empresaPayload = {
        ...empresa,
        codigoIBGE: codigoIBGE || "",
        inscricaoEstadual: ieIsento ? "ISENTO" : (empresa.inscricaoEstadual || ""),
        certificadoStatus: "pendente",
        userId: cred.user.uid,
        login_responsavel: loginPrefeitura || null,
        senha_responsavel: senhaPrefeitura || null,
        criadoEm: serverTimestamp(),
      };

      // 3) grava empresa + perfil
      const empresaRef = await addDoc(collection(db, "empresas"), empresaPayload);
      localStorage.setItem("empresaId", empresaRef.id);

      await setDoc(doc(db, "users", cred.user.uid), {
        nome: nomeResp,
        email: emailResp,
        empresaId: empresaRef.id,
        funcao: "dono_oficina",
        criadoEm: serverTimestamp(),
      });

      // 4) aplica claims e garante que o contexto reflita sem F5
      try {
        await atualizarMinhasClaims();               // servidor define empresaId/funcao nos claims
        await auth.currentUser?.getIdToken(true);    // traz os claims ao token local
      } catch (claimErr) {
        // Pode falhar (ex.: callable indisponível/CORS). Fallback pelo contexto.
        console.warn("Callable atualizarMinhasClaims falhou:", claimErr);
      }
      // força nosso contexto a capturar (claims ou Firestore como fallback)
      await refreshClaims(7000);

      await Swal.fire({
        icon: "success",
        title: "Cadastro realizado!",
        text: "Agora faça login para finalizar o envio do certificado digital.",
        confirmButtonText: "OK",
      });

      navigate("/login");
    } catch (err) {
      console.error("Falha no cadastro:", err);
      if (err?.code === "auth/email-already-in-use")
        setErros({ emailResp: "E-mail já cadastrado" });
      else if (err?.code === "auth/weak-password")
        setErros({ senha: "Senha fraca, mínimo 6 caracteres" });
      else
        setErros({ submit: err?.message || "Erro inesperado ao cadastrar." });

      // Aviso central
      Swal.fire({
        icon: "error",
        title: "Falha no cadastro",
        text: err?.message || "Não foi possível concluir o cadastro.",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVoltarLogin = () => navigate("/login");

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#f9fafb] px-2 relative">
      {/* Botão voltar para login */}
      <button
        onClick={handleVoltarLogin}
        className="absolute top-5 left-5 text-[#004AAD] hover:underline font-semibold text-sm"
        type="button"
      >
        &larr; Voltar para login
      </button>

      <form onSubmit={handleSubmit} className="flex flex-col items-center w-full" autoComplete="off">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 mb-2 w-full max-w-5xl">
          {/* Dados da Empresa */}
          <section className="bg-white rounded-2xl shadow-md border border-blue-50 p-6 w-full flex flex-col gap-4">
            <div className="flex items-center gap-2 mb-2">
              <FaBuilding className="text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-800">Dados da Empresa</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* CNPJ */}
              <div className="flex flex-col">
                <Campo
                  icon={FaIdCard}
                  label="CNPJ"
                  placeholder="CNPJ"
                  mask="99.999.999/9999-99"
                  value={empresa.cnpj}
                  onChange={(e) => setEmpresa((f) => ({ ...f, cnpj: e.target.value }))}
                  error={erros.cnpj}
                />
                {erros.cnpj && <span className="text-red-500 text-xs mt-1">{erros.cnpj}</span>}
                <button
                  type="button"
                  onClick={buscarDadosPorCNPJ}
                  className="mt-1 flex items-center gap-2 px-5 py-1.5 rounded-md text-xs font-medium bg-[#004AAD] text-white hover:bg-blue-800 transition shadow disabled:bg-gray-300 disabled:text-gray-500"
                  disabled={buscandoCNPJ || !validarCNPJ(empresa.cnpj)}
                  title="Buscar informações automáticas na Receita"
                >
                  <FaSearch className="text-xs" />
                  {buscandoCNPJ ? "Buscando..." : "Buscar informações do CNPJ"}
                </button>
              </div>

              <Campo
                icon={FaIdCard}
                label="Razão Social"
                placeholder="Razão Social"
                value={empresa.razaoSocial}
                onChange={(e) => setEmpresa((f) => ({ ...f, razaoSocial: e.target.value }))}
                error={erros.razaoSocial}
              />
              <Campo
                icon={FaIdCard}
                label="Nome Fantasia"
                placeholder="Nome Fantasia"
                value={empresa.nomeFantasia}
                onChange={(e) => setEmpresa((f) => ({ ...f, nomeFantasia: e.target.value }))}
                error={erros.nomeFantasia}
              />

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Regime Tributário</label>
                <div className="flex items-center relative">
                  <FaBuilding className="absolute left-3 text-gray-400" />
                  <select
                    className="pl-9 border rounded-lg w-full p-2.5 bg-white outline-none focus:ring-2 focus:ring-blue-100"
                    value={empresa.regimeTributario}
                    onChange={(e) => setEmpresa((f) => ({ ...f, regimeTributario: e.target.value }))}>
                    <option value="1">1 - Simples Nacional</option>
                    <option value="2">2 - Simples Nacional (Excesso Sublimite)</option>
                    <option value="3">3 - Regime Normal</option>
                    <option value="4">4 - MEI</option>
                  </select>
                </div>
              </div>

              {/* Inscrição Estadual */}
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Inscrição Estadual <span className="text-red-600">*</span>
                </label>
                <div className="flex items-center gap-5 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="ie_isento"
                      checked={ieIsento}
                      onChange={() => setIeIsento(true)}
                      className="accent-[#004072]"
                    />
                    <span>Isento</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="ie_isento"
                      checked={!ieIsento}
                      onChange={() => setIeIsento(false)}
                      className="accent-[#004072]"
                    />
                    <span>Possui IE</span>
                  </label>
                </div>

                {ieIsento ? (
                  <Campo icon={FaHashtag} label="Inscrição Estadual" placeholder="ISENTO" value="ISENTO" readOnly disabled />
                ) : (
                  <Campo
                    icon={FaHashtag}
                    label="Inscrição Estadual"
                    placeholder="Digite o número da IE"
                    value={empresa.inscricaoEstadual || ""}
                    onChange={(e) =>
                      setEmpresa((f) => ({ ...f, inscricaoEstadual: e.target.value.replace(/\D/g, "") }))
                    }
                    error={erros.inscricaoEstadual}
                  />
                )}
                {erros.inscricaoEstadual && <span className="text-red-500 text-xs">{erros.inscricaoEstadual}</span>}
              </div>

              <Campo
                icon={FaIdCard}
                label="Inscrição Municipal"
                placeholder="Inscrição Municipal"
                value={empresa.inscricaoMunicipal}
                onChange={(e) => setEmpresa((f) => ({ ...f, inscricaoMunicipal: e.target.value }))}
                error={erros.inscricaoMunicipal}
              />
              {/* Novos campos fiscais */}
              <Campo
                icon={FaIdCard}
                label="CNAE Principal"
                placeholder="Ex: 4520-0/01"
                value={empresa.cnae}
                onChange={(e) => setEmpresa((f) => ({ ...f, cnae: e.target.value }))}
                error={erros.cnae}
              />
              <Campo
                icon={FaIdCard}
                label="Código Tributário Municipal"
                placeholder="Ex: 620910000"
                value={empresa.codigoTributarioMunicipio}
                onChange={(e) => setEmpresa((f) => ({ ...f, codigoTributarioMunicipio: e.target.value }))}
                error={erros.codigoTributarioMunicipio}
              />
              <Campo
                icon={FaIdCard}
                label="Item da Lista de Serviço (LC 116/03)"
                placeholder="Ex: 14.01"
                value={empresa.itemListaServico}
                onChange={(e) => setEmpresa((f) => ({ ...f, itemListaServico: e.target.value }))}
                error={erros.itemListaServico}
              />
              {/* Fim novos campos */}
              <Campo
                icon={FaEnvelope}
                label="E-mail da empresa"
                placeholder="E-mail da empresa"
                value={empresa.email}
                onChange={(e) => setEmpresa((f) => ({ ...f, email: e.target.value }))}
                error={erros.email}
              />
              <Campo
                icon={FaPhone}
                label="Telefone"
                placeholder="Telefone"
                mask="(99) 99999-9999"
                value={empresa.telefone}
                onChange={(e) => setEmpresa((f) => ({ ...f, telefone: e.target.value }))}
                error={erros.telefone}
              />
            </div>
          </section>

          {/* Endereço */}
          <section className="bg-white rounded-2xl shadow-md border border-blue-50 p-6 w-full flex flex-col gap-4">
            <div className="flex items-center gap-2 mb-2">
              <FaMapMarkedAlt className="text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-800">Endereço</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Campo
                icon={FaLocationArrow}
                label="CEP"
                placeholder="CEP"
                mask="99999-999"
                value={empresa.cep}
                onBlur={handleCepBlur}
                onChange={(e) => setEmpresa((f) => ({ ...f, cep: e.target.value }))}
                error={erros.cep}
              />
              <Campo
                icon={FaMapMarkedAlt}
                label="Endereço"
                placeholder="Endereço"
                value={empresa.endereco}
                onChange={(e) => setEmpresa((f) => ({ ...f, endereco: e.target.value }))}
                error={erros.endereco}
              />
              <Campo
                icon={FaIdCard}
                label="Número"
                placeholder="Número"
                value={empresa.numero}
                onChange={(e) => setEmpresa((f) => ({ ...f, numero: e.target.value }))}
                error={erros.numero}
              />
              <Campo
                icon={FaIdCard}
                label="Complemento"
                placeholder="Complemento"
                value={empresa.complemento}
                onChange={(e) => setEmpresa((f) => ({ ...f, complemento: e.target.value }))}
              />
              <Campo
                icon={FaCity}
                label="Bairro"
                placeholder="Bairro"
                value={empresa.bairro}
                onChange={(e) => setEmpresa((f) => ({ ...f, bairro: e.target.value }))}
                error={erros.bairro}
              />

              <div className="relative">
                <Campo
                  icon={FaCity}
                  label="Cidade"
                  placeholder="Cidade"
                  value={empresa.cidade}
                  onChange={handleCidadeChange}
                  autoComplete="off"
                  error={erros.cidade}
                />
                {sugestoesCidade.length > 0 && (
                  <div className="absolute z-20 bg-white border w-full mt-1 rounded shadow max-h-32 overflow-y-auto">
                    {sugestoesCidade.map((s, i) => (
                      <div
                        key={i}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                        onClick={() => handleSelecionaCidade(s)}
                      >
                        {s}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Campo
                icon={FaCity}
                label="Estado (UF)"
                placeholder="ESTADO (UF)"
                value={empresa.estado}
                onChange={handleEstadoChange}
                maxLength={2}
                error={erros.estado}
                style={{ textTransform: "uppercase" }}
              />
              <Campo
                icon={FaIdCard}
                label="Código IBGE "
                placeholder="Código IBGE"
                value={empresa.codigoIBGE}
                readOnly
                error={erros.codigoIBGE}
              />
            </div>
          </section>
        </div>

        {/* Responsável */}
        <section className="bg-white rounded-2xl shadow-md border border-blue-50 p-6 w-full max-w-5xl mt-4 flex flex-col gap-4">
          <div className="flex items-center gap-2 mb-2">
            <FaUserAlt className="text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-800">Responsável pelo cadastro</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Campo
              icon={FaUserAlt}
              label="Nome completo"
              placeholder="Seu nome completo (responsável)"
              value={nomeResp}
              onChange={(e) => setNomeResp(e.target.value)}
              error={erros.nomeResp}
            />
            <Campo
              icon={FaEnvelope}
              label="E-mail (login)"
              placeholder="Seu e-mail (login)"
              value={emailResp}
              onChange={(e) => setEmailResp(e.target.value)}
              error={erros.emailResp}
            />
            <Campo
              icon={FaKey}
              label="Senha de acesso"
              placeholder="Senha de acesso"
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              error={erros.senha}
            />
            {/* Novos campos login/senha prefeitura (opcional) */}
            <Campo
              icon={FaKey}
              label="Login Prefeitura (opcional)"
              placeholder="Usuário prefeitura (se exigido pela cidade)"
              value={loginPrefeitura}
              onChange={(e) => setLoginPrefeitura(e.target.value)}
            />
            <Campo
              icon={FaKey}
              label="Senha Prefeitura (opcional)"
              placeholder="Senha prefeitura (se exigido pela cidade)"
              type="password"
              value={senhaPrefeitura}
              onChange={(e) => setSenhaPrefeitura(e.target.value)}
            />
          </div>

          <span className="text-xs text-gray-500 mt-1 mb-2">
            <b>Atenção:</b> O login e senha da prefeitura só são necessários para cidades que exigem acesso extra
            para emissão de NFS-e. Se não souber, poderá preencher depois.
          </span>

          <button
            type="submit"
            className={`w-full py-3 rounded-lg mt-4 text-white font-bold shadow transition ${
              loading ? "bg-gray-400 cursor-not-allowed" : "bg-[#004AAD] hover:bg-blue-800 hover:scale-[1.03]"
            }`}
            disabled={loading}
          >
            {loading ? "Cadastrando..." : "Cadastrar"}
          </button>

          {erros.submit && <div className="text-center text-red-600 mt-2">{erros.submit}</div>}
        </section>
      </form>

      <style>{`
        .animate-fade-in { animation: fadeIn 0.7s cubic-bezier(.39,.575,.565,1) both; }
        @keyframes fadeIn { 0% { opacity:0; transform: translateY(30px) } 100% { opacity:1; transform: translateY(0) } }
      `}</style>
    </div>
  );
};

export default Register;
